import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Lock, Eye, EyeOff, Check, X, Shield } from "lucide-react";

const RULES = [
  { test: (p) => p.length >= 8, label: "At least 8 characters" },
  { test: (p) => /[A-Z]/.test(p), label: "One uppercase letter" },
  { test: (p) => /[a-z]/.test(p), label: "One lowercase letter" },
  { test: (p) => /[0-9]/.test(p), label: "One digit" },
  { test: (p) => /[!@#$%^&*()_+\-=\[\]{}|;:'",.<>?/`~\\]/.test(p), label: "One special character" },
];

export default function ChangePasswordModal({ forced = false, onClose }) {
  const { setMustChangePassword, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const allRulesPass = RULES.every((r) => r.test(newPassword));
  const passwordsMatch = newPassword && newPassword === confirmPassword;
  const canSubmit = currentPassword && allRulesPass && passwordsMatch && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSaving(true);
    try {
      await api.put("/api/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccess(true);
      setMustChangePassword(false);
      setTimeout(() => {
        if (forced) {
          window.location.reload();
        } else if (onClose) {
          onClose();
        }
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to change password");
    }
    setSaving(false);
  };

  return (
    <div
      data-testid="change-password-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)",
      }}
      onClick={forced ? undefined : onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "hsl(var(--panel, 220 15% 10%))",
          border: "1px solid hsl(var(--border, 220 10% 20%))",
          borderRadius: "16px", padding: "32px", width: "420px", maxWidth: "95vw",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
          <div style={{
            width: "40px", height: "40px", borderRadius: "10px",
            background: forced ? "hsl(var(--err, 0 80% 55%) / 0.15)" : "hsl(var(--primary, 190 100% 50%) / 0.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Shield size={20} style={{ color: forced ? "hsl(var(--err, 0 80% 55%))" : "hsl(var(--primary, 190 100% 50%))" }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "hsl(var(--ink, 0 0% 95%))" }}>
              {forced ? "Password Change Required" : "Change Password"}
            </h2>
            <p style={{ margin: 0, fontSize: "12px", color: "hsl(var(--ink-muted, 0 0% 55%))" }}>
              {forced ? "You must set a new password before continuing" : "Update your account password"}
            </p>
          </div>
          {!forced && (
            <button onClick={onClose} style={{
              marginLeft: "auto", background: "none", border: "none", cursor: "pointer",
              color: "hsl(var(--ink-muted))", padding: "4px",
            }}>
              <X size={18} />
            </button>
          )}
        </div>

        {success ? (
          <div style={{
            textAlign: "center", padding: "24px",
            background: "hsl(var(--ok, 140 70% 45%) / 0.1)",
            borderRadius: "10px", border: "1px solid hsl(var(--ok) / 0.2)",
          }}>
            <Check size={32} style={{ color: "hsl(var(--ok))", margin: "0 auto 8px" }} />
            <p style={{ fontWeight: 600, color: "hsl(var(--ok))" }}>Password changed successfully!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div data-testid="change-password-error" style={{
                padding: "10px 14px", marginBottom: "16px", borderRadius: "8px",
                background: "hsl(var(--err) / 0.1)", border: "1px solid hsl(var(--err) / 0.2)",
                color: "hsl(var(--err))", fontSize: "13px",
              }}>
                {error}
              </div>
            )}

            {/* Current Password */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "hsl(var(--ink-muted))", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Current Password
              </label>
              <div style={{ position: "relative" }}>
                <Lock size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "hsl(var(--ink-muted))" }} />
                <input
                  data-testid="current-password-input"
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  style={{
                    width: "100%", padding: "10px 40px 10px 36px", borderRadius: "8px",
                    border: "1px solid hsl(var(--border))", background: "hsl(var(--surface, 220 15% 8%))",
                    color: "hsl(var(--ink))", fontSize: "14px", outline: "none", boxSizing: "border-box",
                  }}
                />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)} style={{
                  position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer", color: "hsl(var(--ink-muted))", padding: "2px",
                }}>
                  {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "hsl(var(--ink-muted))", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                New Password
              </label>
              <div style={{ position: "relative" }}>
                <Lock size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "hsl(var(--ink-muted))" }} />
                <input
                  data-testid="new-password-input"
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  style={{
                    width: "100%", padding: "10px 40px 10px 36px", borderRadius: "8px",
                    border: `1px solid ${newPassword ? (allRulesPass ? "hsl(var(--ok) / 0.5)" : "hsl(var(--warn) / 0.5)") : "hsl(var(--border))"}`,
                    background: "hsl(var(--surface))", color: "hsl(var(--ink))", fontSize: "14px",
                    outline: "none", boxSizing: "border-box",
                  }}
                />
                <button type="button" onClick={() => setShowNew(!showNew)} style={{
                  position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer", color: "hsl(var(--ink-muted))", padding: "2px",
                }}>
                  {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>

              {/* Password Rules */}
              {newPassword && (
                <div style={{ marginTop: "8px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px" }}>
                  {RULES.map((rule) => {
                    const pass = rule.test(newPassword);
                    return (
                      <div key={rule.label} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px" }}>
                        {pass ? (
                          <Check size={12} style={{ color: "hsl(var(--ok))", flexShrink: 0 }} />
                        ) : (
                          <X size={12} style={{ color: "hsl(var(--err))", flexShrink: 0 }} />
                        )}
                        <span style={{ color: pass ? "hsl(var(--ok))" : "hsl(var(--ink-muted))" }}>{rule.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "hsl(var(--ink-muted))", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Confirm New Password
              </label>
              <div style={{ position: "relative" }}>
                <Lock size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "hsl(var(--ink-muted))" }} />
                <input
                  data-testid="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  style={{
                    width: "100%", padding: "10px 12px 10px 36px", borderRadius: "8px",
                    border: `1px solid ${confirmPassword ? (passwordsMatch ? "hsl(var(--ok) / 0.5)" : "hsl(var(--err) / 0.5)") : "hsl(var(--border))"}`,
                    background: "hsl(var(--surface))", color: "hsl(var(--ink))", fontSize: "14px",
                    outline: "none", boxSizing: "border-box",
                  }}
                />
              </div>
              {confirmPassword && !passwordsMatch && (
                <p style={{ fontSize: "11px", color: "hsl(var(--err))", marginTop: "4px" }}>Passwords do not match</p>
              )}
            </div>

            <button
              data-testid="change-password-submit"
              type="submit"
              disabled={!canSubmit}
              style={{
                width: "100%", padding: "12px", borderRadius: "10px", border: "none",
                background: canSubmit ? "hsl(var(--primary))" : "hsl(var(--surface))",
                color: canSubmit ? "#000" : "hsl(var(--ink-muted))",
                fontSize: "14px", fontWeight: 700, cursor: canSubmit ? "pointer" : "not-allowed",
                transition: "all 0.2s ease",
              }}
            >
              {saving ? "Changing Password..." : "Change Password"}
            </button>

            {forced && (
              <button
                type="button"
                onClick={logout}
                data-testid="change-password-logout"
                style={{
                  width: "100%", padding: "10px", marginTop: "10px", borderRadius: "10px",
                  border: "1px solid hsl(var(--border))", background: "transparent",
                  color: "hsl(var(--ink-muted))", fontSize: "13px", cursor: "pointer",
                }}
              >
                Sign Out Instead
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
